import { ReactElement, useEffect, useMemo } from 'react';
import Script from 'next/script';

import { useCart } from '~/lib/cart';
import { KLARNA_APPROVED_COUNTRIES } from '~/constants/checkout';

export function KlarnaMessage({
  className = '',
  currentBillingCountry = '',
}: Readonly<{
  className?: string;
  currentBillingCountry?: string;
}>): ReactElement {
  const { cart } = useCart();
  const KLARNA_MESSAGING_CLIENT_ID =
    process?.env?.NEXT_PUBLIC_KLARNA_MESSAGING_CLIENT_ID ?? '';
  const klarnaCartTotal = Math.round((cart?.total ?? 0) * 100);
  const klarnaBillingCountry = useMemo(
    () =>
      // Use en-US for now since we are only supporting USD and English
      KLARNA_APPROVED_COUNTRIES.includes(currentBillingCountry) ? 'en-US' : '',
    [currentBillingCountry],
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && klarnaCartTotal) {
      // @ts-ignore -- This is being used by the Klarna script below
      window?.Klarna?.OnsiteMessaging?.refresh();
    }
  }, [klarnaCartTotal, klarnaBillingCountry]); // Update Klarna if country changes

  if (!klarnaBillingCountry || klarnaCartTotal <= 0) return <></>;

  return (
    <>
      <Script
        strategy="lazyOnload"
        data-environment={
          process?.env?.NODE_ENV === 'production' ? 'production' : 'playground'
        }
        src="https://osm.klarnaservices.com/lib.js"
        data-client-id={KLARNA_MESSAGING_CLIENT_ID}
      />
      <div className={className} data-testid="klarna-container">
        {/* @ts-ignore -- This is being used by the Klarna script */}
        <klarna-placement
          id="klarna-center-strip"
          data-key="credit-promotion-auto-size"
          data-locale={klarnaBillingCountry}
          data-purchase-amount={klarnaCartTotal}
        />
      </div>
    </>
  );
}
